import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import {
  isToday,
  format,
  isValid,
  getHours,
  getMinutes,
  getYear,
  parse,
} from 'date-fns';
import { frCA, enCA } from 'date-fns/locale';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl';
import get from 'lodash.get';
import { useAfterFirstRender } from '../../hooks/useAfterFirstRender';
import { Icons } from '../';
import { applyClasses, applyProps } from '../shared/js';
import styles from './datetime.module.scss';

registerLocale('fr', frCA);
registerLocale('en', enCA);

const STARTING_YEAR = 1980;
const ENDING_YEAR_OFFSET = 30;

const DateTime = ({
  id,
  maxDate,
  minDate,
  peekNextMonth,
  onChange,
  onClose,
  name,
  validation,
  disabled,
  selectYear,
  time,
  defaultValue,
  label,
  mobileLabel,
  shouldCloseOnSelect,
  placeholder,
  value,
  ...props
}) => {
  const { locale } = useIntl();
  const formMethods = useFormContext();
  const inputDefault =
    value || get(formMethods?.getValues(), name) || defaultValue;

  const [selectedDate, setSelectedDate] = useState(inputDefault);
  const calendarRef = useRef();
  const register = formMethods?.register ? formMethods?.register : () => {};
  const setFormValue = formMethods?.setValue ? formMethods?.setValue : () => {};
  const formProps = { ...register(name, { ...validation }) };

  const handleChange = useCallback(
    (date) => {
      setSelectedDate(date);
      onChange && onChange(date);
      setFormValue &&
        setFormValue(name, date, { shouldValidate: true, shouldDirty: true });
    },
    [name, onChange] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    value && handleChange(value);
  }, [value, handleChange]);

  const errors = formMethods?.formState.errors;
  const error = errors && get(errors, name);

  const dayClassName = (date) => {
    return cn(styles.day, { [styles.today]: isToday(date) });
  };

  const closePopup = () => {
    calendarRef.current && calendarRef.current.setOpen(false);
  };

  const dayContentsRenderer = (day) =>
    `${parseInt(day, 10) < 10 ? '0' : ''}${day}`;

  const classes = applyClasses(props, styles);

  return (
    <div
      // eslint-disable-next-line react/prop-types
      className={cn(styles.datepickerWrapper, props.className, {
        [styles.hasSelected]: !!selectedDate,
      })}
    >
      {label && (
        <label className={styles.label} htmlFor={`input_${name}`}>
          {label}
        </label>
      )}
      <DatePicker
        locale={locale}
        showTimeInput={time}
        ref={calendarRef}
        selected={selectedDate}
        minDate={minDate}
        maxDate={maxDate}
        peekNextMonth={peekNextMonth}
        shouldCloseOnSelect={shouldCloseOnSelect}
        placeholderText={placeholder}
        customInput={
          <DatepickerToggle
            formProps={formProps}
            error={error}
            defaultValue={inputDefault}
            buttonId={id}
            disabled={disabled}
            time={time}
            {...props}
          />
        }
        customTimeInput={<TimeInput />}
        className={classes}
        wrapperClassName={styles.datepicker}
        calendarClassName={cn(styles.calendar, classes)}
        dayClassName={dayClassName}
        dateFormat={time ? 'yyyy-MM-dd h:mm aa' : 'yyyy-MM-dd'}
        renderCustomHeader={(props) => (
          <DatepickerHeader
            closePopup={closePopup}
            id={id}
            selectYear={selectYear}
            label={mobileLabel ? mobileLabel : label}
            maxDate={maxDate}
            minDate={minDate}
            {...props}
          />
        )}
        formatWeekDay={(day) =>
          `${day.substring(0, 3)}${locale === 'fr' ? '.' : ''}`
        }
        popperClassName={styles.calendarContainer}
        value={get(formMethods?.getValues(), name)}
        {...applyProps(props)}
        onChange={handleChange}
        disabled={disabled}
        onCalendarClose={() => {
          document.body.classList.remove(styles.scrollLockMobile);
          onClose?.(selectedDate);
        }}
        onCalendarOpen={() =>
          document.body.classList.add(styles.scrollLockMobile)
        }
        renderDayContents={dayContentsRenderer}
      />
    </div>
  );
};

const DatepickerToggle = React.forwardRef(
  (
    {
      value,
      className,
      inputName,
      formProps,
      error,
      defaultValue,
      disabled,
      placeholder,
      time,
      ...props
    },
    ref
  ) => {
    const { formatMessage, locale } = useIntl();
    const accessibleLabel = formatMessage({
      id: 'forms.datepicker.open',
      defaultMessage: 'Open the datepicker',
      description: 'Label to describe the functionality of the toggle button',
    });

    const label = useMemo(() => {
      const dateFormat = locale === 'fr' ? 'dd-MM-yyyy' : 'yyyy-MM-dd';
      const timeFormat = time ? ' hh:mm a' : '';

      const labelFormat = `${dateFormat}${timeFormat}`;
      if (!value && defaultValue) return format(defaultValue, labelFormat);
      else if (!value) return placeholder;
      else if (value instanceof Date) return format(value, labelFormat);
      const date = time
        ? parse(value, 'yyyy-MM-dd hh:mm a', new Date())
        : parse(value, 'yyyy-MM-dd', new Date());
      return format(date, labelFormat);
    }, [locale, value]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <>
        <input
          type="hidden"
          value={label || ''}
          name={inputName}
          {...formProps}
        />
        <button
          id={props.buttonId}
          ref={ref}
          type="button"
          className={cn(className, styles.toggle, {
            [styles.disabled]: disabled,
          })}
          {...applyProps(props)}
          aria-label={accessibleLabel}
        >
          {label}
          <div className={styles.icon}>
            <Icons.Calendar />
          </div>
        </button>
        {error && (
          <span role="alert">
            {error.message || (
              <FormattedMessage
                id="forms.error"
                description="Error message"
                defaultMessage="Field error"
              />
            )}
          </span>
        )}
      </>
    );
  }
);

DatepickerToggle.displayName = 'DateTime';

const DatepickerHeader = ({
  id,
  date,
  decreaseMonth,
  increaseMonth,
  closePopup,
  selectYear,
  changeYear,
  label,
  minDate,
  maxDate,
}) => {
  const currentYear = parseInt(format(date, 'yyyy'));
  const { formatMessage } = useIntl();
  const accessibleLabel = formatMessage({
    id: 'forms.datepicker.close',
    defaultMessage: 'Close the datepicker',
    description: 'Label to describe the functionality of the close button',
  });

  const years = () => {
    const endYear = maxDate
      ? getYear(maxDate)
      : currentYear + ENDING_YEAR_OFFSET;
    const years = [];

    for (
      let year = minDate ? getYear(minDate) : STARTING_YEAR;
      year <= endYear;
      year++
    ) {
      years.push(
        <option key={year} value={`${year}`}>
          {year}
        </option>
      );
    }
    return years;
  };
  return (
    <>
      <div className={styles.titleBar}>
        <button
          type="button"
          className={cn(styles.button, styles.close)}
          onClick={closePopup}
          aria-label={accessibleLabel}
          id={id && `${id}_close`}
        >
          <span className={styles.closeBtnLabel}>
            <FormattedMessage id="dateTime.close" defaultMessage="Close" />
          </span>
          <Icons.Close />
        </button>
      </div>
      {label && (
        <div className={styles.dateTimeHeaderLabelContainer}>
          <label>{label}</label>
        </div>
      )}

      <div className={styles.header}>
        <button
          id={id && `${id}_prev`}
          type="button"
          className={cn(styles.button, styles.prev)}
          onClick={decreaseMonth}
        >
          <Icons.Chevron />
        </button>
        <span id={id && `${id}_date`} className={styles.date}>
          {selectYear ? (
            <FormattedDate value={date} month="long" />
          ) : (
            <FormattedDate value={date} month="long" year="numeric" />
          )}
        </span>
        <button
          id={id && `${id}_next`}
          type="button"
          className={cn(styles.button, styles.next)}
          onClick={increaseMonth}
        >
          <Icons.Chevron />
        </button>
        {selectYear && (
          <div className={styles.yearDropdown}>
            <select
              onChange={({ target: { value: year } }) => changeYear(year)}
              value={currentYear}
            >
              {years()}
            </select>
            <div className={styles.dropdownArrow}>
              <Icons.ChevronDown />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const TimeInput = ({ date, onChange }) => {
  const defaultHourFromDate = () => {
    if (!isValid(date)) return '';
    else if (getHours(date) === 0) return '12';
    else if (getHours(date > 22)) return `${getHours(date) - 12}`;
    else if (getHours(date) > 12) return `0${getHours(date) - 12}`;
    else if (getHours(date) < 10) return `0${getHours(date)}`;
    return `${getHours(date)}`;
  };

  const defaultMinuteFromDate = () => {
    if (!isValid(date)) return '';
    else if (`${getMinutes(date)}`.length === 1) return `0${getMinutes(date)}`;

    return `${getMinutes(date)}`;
  };

  const defaultAmPm = () => {
    if (!isValid(date)) return 'am';
    if (getHours(date) >= 12) return 'pm';

    return 'am';
  };

  const [hour, setHour] = useState(defaultHourFromDate());
  const [minute, setMinute] = useState(defaultMinuteFromDate());
  const [ampm, setAmpm] = useState(defaultAmPm());
  const mobileInputRef = useRef();

  useAfterFirstRender(() => {
    let twentyFourHour;
    if (hour === '') twentyFourHour = 0;
    else if (hour === 'HH') twentyFourHour = 1;
    else if (hour === '12' && ampm === 'am') twentyFourHour = 0;
    else if (hour === '12') twentyFourHour = 12;
    else if (ampm === 'pm') twentyFourHour = parseInt(hour) + 12;
    else twentyFourHour = hour;

    onChange(`${twentyFourHour}:${minute === 'MM' ? 0 : minute}`);
  }, [hour, minute, ampm, onChange]);

  useAfterFirstRender(() => {
    if (hour && hour !== 'HH') return;
    setHour('12');
  }, [minute]);

  useAfterFirstRender(() => {
    if (minute && minute !== 'MM') return;
    setMinute('00');
  }, [hour]);

  const handleHourChange = (e) => {
    const hour = e.target.value;
    const num = parseInt(hour);
    if (
      hour !== '' &&
      (hour.length > 2 ||
        isNaN(num) ||
        (num < 0) | (num > 12) ||
        hour.includes('.') ||
        hour.includes('-'))
    )
      return;
    setHour(hour);
  };

  const handleMinuteChange = (e) => {
    const minute = e.target.value;
    const num = parseInt(minute);
    if (
      minute !== '' &&
      (minute.length > 2 ||
        isNaN(num) ||
        (num < 0) | (num > 59) ||
        minute.includes('.') ||
        minute.includes('-'))
    )
      return;
    setMinute(minute);
  };

  const increaseHour = () => {
    const oldHour = parseInt(hour);
    if (oldHour >= 12) return;
    const newHour = hour === '' || hour === 'HH' ? 1 : oldHour + 1;
    setHour(`${newHour < 10 ? '0' : ''}${newHour}`);
  };

  const decreaseHour = () => {
    const oldHour = parseInt(hour);
    if (oldHour <= 1) return;
    setHour(`${hour < 11 ? '0' : ''}${oldHour - 1}`);
  };

  const increaseMinute = () => {
    const oldMinute = parseInt(minute);
    if (oldMinute >= 59) return;
    const newMinute = minute === '' || minute === 'MM' ? 1 : oldMinute + 1;
    setMinute(`${newMinute < 10 ? '0' : ''}${newMinute}`);
  };

  const decreaseMinute = () => {
    const oldMinute = parseInt(minute);
    if (oldMinute <= 0) return;
    setMinute(`${minute < 11 ? '0' : ''}${oldMinute - 1}`);
  };

  const toggleAmpm = () => {
    setAmpm(ampm === 'am' ? 'pm' : 'am');
  };

  return (
    <>
      <div className={styles.timeInputContainer}>
        <input
          type="number"
          min="0"
          max="12"
          maxLength={2}
          className={styles.timeInput}
          placeholder="HH"
          value={hour}
          onChange={handleHourChange}
        />
        <div className={styles.timeArrowsContainer}>
          <div className={styles.timeArrows}>
            <button
              className="hourUp"
              type="button"
              onClick={increaseHour}
              disabled={parseInt(hour) >= 12}
            />
            <button
              className="hourDown"
              type="button"
              onClick={decreaseHour}
              disabled={hour === '' || hour === 'HH' || parseInt(hour) <= 1}
            />
          </div>
          :
        </div>
        <input
          type="number"
          min="0"
          max="59"
          maxLength={2}
          className={styles.timeInput}
          placeholder="MM"
          onChange={handleMinuteChange}
          value={minute}
        />
        <div className={styles.timeArrowsContainer}>
          <div className={styles.timeArrows}>
            <button
              className="minuteUp"
              type="button"
              onClick={increaseMinute}
              disabled={parseInt(minute) >= 59}
            />
            <button
              className="minuteDown"
              type="button"
              onClick={decreaseMinute}
              disabled={
                minute === '' || minute === 'MM' || parseInt(minute) <= 0
              }
            />
          </div>
        </div>
        <button className={styles.ampm} onClick={toggleAmpm} type="button">
          {ampm === 'pm' ? 'PM' : 'AM'}
        </button>
      </div>
      <input
        className={styles.mobileTimeInput}
        ref={mobileInputRef}
        type="time"
        onChange={(e) => {
          e.target.value && onChange(e.target.value);
        }}
        value={`${getHours(date)}:${getMinutes(date)}`}
      />
    </>
  );
};

DateTime.propTypes = {
  /** The ID of the input. Useful to select the input during testing. */
  id: PropTypes.string,
  /** The latest date that can be selected */
  maxDate: PropTypes.instanceOf(Date),
  /** The earliest date that can be selected */
  minDate: PropTypes.instanceOf(Date),
  /** Shows the first week of next month */
  peekNextMonth: PropTypes.bool,
  /** Callback function that is called when a date is selected */
  onChange: PropTypes.func,
  /** Callback function that is called when the picker is closed */
  onClose: PropTypes.func,
  /** The name of the input for use in a form */
  name: PropTypes.string,
  /** Applies primary styles to the component */
  primary: PropTypes.bool,
  /** Applies secondary styles to the component */
  secondary: PropTypes.bool,
  /** Applies tertiary styles to the component */
  tertiary: PropTypes.bool,
  /** The validation rules for the input, see https://react-hook-form.com/get-started#Applyvalidation */
  validation: PropTypes.object,
  /** The form errors object */
  errors: PropTypes.object,
  /** Disable the input */
  disabled: PropTypes.bool,
  /** A default value for the input */
  defaultValue: PropTypes.any,
  /** Show a dropdown to select a year */
  selectYear: PropTypes.bool,
  /** Show the time picker */
  time: PropTypes.bool,
  /** An optional small label above the input */
  label: PropTypes.any,
  /** A label that should appear in the mobile picker only. Will default to the label prop if not provided */
  mobileLabel: PropTypes.any,
  /** Close the picker after selecting a date */
  shouldCloseOnSelect: PropTypes.bool,
  /** Override the value maintained by the component. The same callback functions will be called as when the user selects a date manually. Try to use the setValue function available from a Form's context instead when possible to not cause confusion about where the value is being maintained. */
  value: PropTypes.instanceOf(Date),
  /** Placeholder for the input when no time is selected */
  placeholder: PropTypes.string,
};

DateTime.defaultProps = {
  id: undefined,
  maxDate: undefined,
  minDate: undefined,
  peekNextMonth: false,
  onChange: undefined,
  name: undefined,
  primary: undefined,
  secondary: undefined,
  tertiary: undefined,
  validation: {},
  errors: {},
  disabled: false,
  defaultValue: undefined,
  selectYear: false,
  time: false,
  shouldCloseOnSelect: true,
};

DatepickerToggle.propTypes = {
  /** The ID of the input. Useful to select the input during testing. */
  buttonId: PropTypes.string,
  /** Callback function called when the toggle is clicked */
  onClick: PropTypes.func,
  /** The value shown in the toggle, the selected value */
  value: PropTypes.string,
  /** A className string forwarded to the toggle button element */
  className: PropTypes.string,
  /** The name of the hidden input */
  inputName: PropTypes.string,
  /** The props created by a containing form element */
  formProps: PropTypes.object,
  /** The error associated with the field */
  error: PropTypes.object,
  defaultValue: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
  ]),
  primary: PropTypes.bool,
  secondary: PropTypes.bool,
  tertiary: PropTypes.bool,
  /** Disable the input */
  disabled: PropTypes.bool,
  /** Placeholder for the input */
  placeholder: PropTypes.string,
  /** Whether or not to show the time */
  time: PropTypes.bool,
};

DatepickerToggle.defaultProps = {
  id: undefined,
  onClick: undefined,
  value: undefined,
  className: undefined,
  inputName: undefined,
  formProps: undefined,
  error: undefined,
  defaultValue: undefined,
  primary: undefined,
  secondary: undefined,
  tertiary: undefined,
  disabled: false,
};

DatepickerHeader.propTypes = {
  /** The ID of the input. Useful to select the input during testing. Also used as a prefix for the associated elements. */
  id: PropTypes.string,
  /** The current date to show the correct month and year */
  date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  /** Callback function called when the previous month button is clicked */
  decreaseMonth: PropTypes.func,
  /** Callback function called when the next month button is clicked */
  increaseMonth: PropTypes.func,
  /** Callback function called when the picker is closed */
  closePopup: PropTypes.func,
  primary: PropTypes.bool,
  secondary: PropTypes.bool,
  tertiary: PropTypes.bool,
  selectYear: PropTypes.bool,
  changeYear: PropTypes.func,
  label: PropTypes.any,
  /** The earliest date to show in the year dropdown */
  minDate: PropTypes.instanceOf(Date),
  /** The last date to show in the year dropdown */
  maxDate: PropTypes.instanceOf(Date),
};

DatepickerHeader.defaultProps = {
  id: undefined,
  date: undefined,
  decreaseMonth: undefined,
  increaseMonth: undefined,
  closePopup: undefined,
  primary: undefined,
  secondary: undefined,
  tertiary: undefined,
  selectYear: false,
  changeYear: undefined,
  label: '',
};

TimeInput.propTypes = {
  date: PropTypes.any,
  value: PropTypes.any,
  onChange: PropTypes.func,
};

export default DateTime;
